/**
 * EmptyState Component
 * Display empty state messages with consistent styling
 */

import React from 'react';
import { Inbox } from 'lucide-react';
import { useAppTranslation } from '@/contexts/TranslationContext';

interface EmptyStateProps {
  icon?: React.ReactNode | React.ComponentType<{ className?: string }>;
  title?: string;
  message?: string;
  description?: string; // Alias for message
  action?: {
    label: string;
    onClick: () => void;
  };
}

export function EmptyState({
  icon,
  title,
  message,
  description,
  action
}: EmptyStateProps) {
  const { t } = useAppTranslation();
  const displayTitle = title || t('ui.noItemsFound', 'No items found');
  const displayMessage = message || description || '';

  // Render icon - handle both React nodes and component types
  const renderIcon = () => {
    if (!icon) {
      return <Inbox className="w-8 h-8 text-gray-400" />;
    }
    // If icon is a component (function), render it as JSX
    if (typeof icon === 'function') {
      const IconComponent = icon as React.ComponentType<{ className?: string }>;
      return <IconComponent className="w-8 h-8 text-gray-400" />;
    }
    // Otherwise it's already a React node
    return icon;
  };

  return (
    <div className="flex flex-col items-center justify-center p-8 text-center">
      <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
        {renderIcon()}
      </div>
      <h3 className="text-lg font-semibold text-gray-900 mb-2">{displayTitle}</h3>
      <p className="text-gray-600 mb-4 max-w-md">{displayMessage}</p>
      {action && (
        <button
          onClick={action.onClick}
          className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
        >
          {action.label}
        </button>
      )}
    </div>
  );
}
